// ============================================
// useGoogleMaps — Shared Google Maps loader hook
// Works with the global <script> tag in index.html
// Replaces useJsApiLoader to prevent loader conflicts
// ============================================

import { useState, useEffect } from 'react';

/**
 * Custom hook that checks if Google Maps API is loaded.
 * The script is loaded via <script> tag in public/index.html.
 * This hook just waits for it to be ready.
 *
 * @returns {{ isLoaded: boolean, loadError: string|null }}
 */
export const useGoogleMaps = () => {
    const [isLoaded, setIsLoaded] = useState(
        window.__googleMapsLoaded__ || !!window.google?.maps
    );
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        if (isLoaded) return;

        let attempts = 0;
        const maxAttempts = 100; // 10 seconds max

        const check = setInterval(() => {
            attempts++;
            if (window.__googleMapsLoaded__ || window.google?.maps) {
                setIsLoaded(true);
                clearInterval(check);
            } else if (attempts >= maxAttempts) {
                setLoadError('Google Maps failed to load. Please refresh the page.');
                clearInterval(check);
            }
        }, 100);

        return () => clearInterval(check);
    }, [isLoaded]);

    return { isLoaded, loadError };
};

export default useGoogleMaps;
